import { ReactElement } from 'react';
import { Button, FormField, RequestTypeBadge, StepIndicator } from '../../../../core/components';
import { tokens } from '../../../../core/theme';
import { REQUEST_TYPE_CONFIG, RequestType } from '../../../../core/types';
import {
  BUTTON_GROUP_GAP,
  DESKTOP_HEADER_GAP,
  FORM_MAX_WIDTH,
  MOBILE_HEADER_GAP,
  TOTAL_STEPS,
} from '../../constants';
import { PersonalDataFieldName, PersonalDataFieldState, useAutoFocusRef } from '../../hooks';
import { PersonalDataFormValues } from '../../types/personalDataForm';
import { useIsDesktop, usePageTitle } from '../../../../core/hooks';

const MOBILE_FORM_GAP = 16;
const DESKTOP_FORM_GAP = 24;

type PersonalDataStepProps = {
  type: RequestType;
  values: PersonalDataFormValues;
  isValid: boolean;
  handleChange: (field: PersonalDataFieldName, rawValue: string) => void;
  handleBlur: (field: PersonalDataFieldName) => void;
  getFieldState: (field: PersonalDataFieldName) => PersonalDataFieldState;
  getFieldError: (field: PersonalDataFieldName) => string | undefined;
  onContinue: () => void;
};

export const PersonalDataStep = ({
  type,
  values,
  isValid,
  handleChange,
  handleBlur,
  getFieldState,
  getFieldError,
  onContinue,
}: PersonalDataStepProps): ReactElement => {
  const isDesktop = useIsDesktop();
  const headingRef = useAutoFocusRef<HTMLHeadingElement>();
  const { icon: requestTypeIcon, label: requestTypeLabel } = REQUEST_TYPE_CONFIG[type];
  usePageTitle(`${requestTypeLabel} — Informe seus dados`);

  return (
    <div style={styles.page}>
      <div style={{ ...styles.form, gap: isDesktop ? DESKTOP_FORM_GAP : MOBILE_FORM_GAP }}>
        <div style={{ ...styles.header, gap: isDesktop ? DESKTOP_HEADER_GAP : MOBILE_HEADER_GAP }}>
          <div style={styles.badgeRow}>
            <RequestTypeBadge icon={requestTypeIcon} label={requestTypeLabel} />
            <StepIndicator currentStep={1} totalSteps={TOTAL_STEPS} />
          </div>
          <h1 ref={headingRef} tabIndex={-1} style={styles.sectionTitle}>
            Informe seus dados
          </h1>
        </div>
        <div style={styles.fields}>
          <FormField
            id="name"
            label="Nome"
            placeholder="Digite seu nome"
            value={values.name}
            state={getFieldState('name')}
            error={getFieldError('name')}
            onChange={(value) => handleChange('name', value)}
            onBlur={() => handleBlur('name')}
          />
          <FormField
            id="cpf"
            label="CPF"
            placeholder="Digite seu CPF"
            value={values.cpf}
            state={getFieldState('cpf')}
            disclaimer="Apenas números"
            error={getFieldError('cpf')}
            onChange={(value) => handleChange('cpf', value)}
            onBlur={() => handleBlur('cpf')}
            inputMode="numeric"
          />
          <FormField
            id="email"
            label="E-mail"
            placeholder="Digite seu e-mail"
            value={values.email}
            state={getFieldState('email')}
            error={getFieldError('email')}
            optional
            onChange={(value) => handleChange('email', value)}
            onBlur={() => handleBlur('email')}
            inputMode="email"
          />
          <FormField
            id="phone"
            label="Telefone"
            placeholder="(00) 00000-0000"
            value={values.phone}
            state={getFieldState('phone')}
            disclaimer="Apenas números"
            error={getFieldError('phone')}
            onChange={(value) => handleChange('phone', value)}
            onBlur={() => handleBlur('phone')}
            inputMode="numeric"
          />
        </div>
        <div style={styles.buttonGroup}>
          <Button variant="secondary" to={'/'}>
            Voltar
          </Button>
          <Button variant="primary" disabled={!isValid} onClick={onContinue}>
            Continuar
          </Button>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    padding: `${tokens.spacing.default} ${tokens.spacing.pageGutter}`,
    paddingBottom: tokens.spacing.large,
    boxSizing: 'border-box',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: FORM_MAX_WIDTH,
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  badgeRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  sectionTitle: {
    margin: tokens.spacing.none,
    fontSize: tokens.typography.fontSize.sectionTitle,
    fontWeight: tokens.typography.fontWeight.semiBold,
    color: tokens.colors.brand.gray.DEFAULT,
    textTransform: 'uppercase',
  },
  fields: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing.medium,
    width: '100%',
  },
  buttonGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: BUTTON_GROUP_GAP,
    width: '100%',
  },
};
